import React from "react";
import { css } from "@emotion/core";

import { useTrackingContext } from "@zapier/toolbox-tracking";

import { defaultCurrency, useCurrencies, Currency } from "@zapier/currency";
import { UseQuery } from "@zapier/checkout/esm/types";
import { sortCurrenciesByArray } from "@zapier/checkout/esm/utils";
import { Dropdown as RebrandDropdown, useTheme } from "@zapier/design-system";

import { Dropdown } from "./Dropdown";
import { FlagIcon } from "./FlagIcon";

import { currencyOrder, PLAN_CHANGE_EVENT } from "./constants";

const rootStyles = css`
  width: 143px;
`;

// Rebrand styles
const containerCss = css`
  width: 160px;
  text-align: left;
  @media (max-width: 375px) {
      width: 100%;
    }
  }
`;

type Props = {
  onSelectCurrency: (currency: Currency) => void;
  selectedCurrency: Currency;
  useQuery: UseQuery;
};

/**
 * A dropdown that allows the user to select a currency to pay in.
 */
export const CurrencyDropdown = ({
  onSelectCurrency,
  selectedCurrency = defaultCurrency,
  useQuery,
}: Props) => {
  const { isRebrand } = useTheme();
  const { trackEvent } = useTrackingContext();
  const { isLoading, currencies: options, error } = useCurrencies({ useQuery });

  const onChangeCurrency = (currency: Currency) => {
    onSelectCurrency(currency);
    // Send event with currency
    trackEvent(PLAN_CHANGE_EVENT, {
      absolute_url: window.location.href,
      event_action: "select_currency",
      interaction_goal: "select currency",
      interaction_name: "currency dropdown",
      option_selected: currency.name,
    });
  };

  // Don't show if loading or error
  if (isLoading || error) {
    return null;
  }

  if (isRebrand) {
    return (
      <div css={containerCss}>
        <RebrandDropdown
          ariaLabel="Currency"
          getLabelForItem={item => `${item.name} ${item.symbol}`}
          items={[...options].sort((a: Currency, b: Currency) =>
            sortCurrenciesByArray(a, b, currencyOrder)
          )}
          menuAriaLabel="List of currencies"
          onChange={(currency: Currency) => onChangeCurrency(currency)}
          placeholder="Choose a currency..."
          renderIcon={item => {
            //@ts-ignore
            return !!item && <FlagIcon name={item.icon} />;
          }}
          // Pull this custom code up from the checkout's Dropdown component
          {...(selectedCurrency
            ? { selectedItem: selectedCurrency }
            : { initialSelectedItem: selectedCurrency })}
        />
      </div>
    );
  }

  return (
    <div css={rootStyles}>
      <Dropdown
        ariaLabel="Currency"
        getLabelForSelectedItem={item => `${item.name} ${item.symbol}`}
        items={[...options].sort((a: Currency, b: Currency) =>
          sortCurrenciesByArray(a, b, currencyOrder)
        )}
        menuAriaLabel="List of currencies"
        onChange={(currency: Currency) => onChangeCurrency(currency)}
        placeholder="Choose a currency..."
        renderIcon={item => {
          return !!item && <FlagIcon name={item.icon} />;
        }}
        selectedItem={selectedCurrency}
      />
    </div>
  );
};
